'use client';
import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const vehicleTypes = [
'Sedan',
'Coupe',
'SUV / Crossover',
'Pickup Truck',
'Tesla Model 3 / Y',
'Tesla Model S / X',
'Luxury Sedan (BMW / Mercedes / Lexus)',
'Fleet Vehicle (company car)',
'Other'];


const serviceTiers = [
{ value: 'maintenance', label: 'Maintenance Wash - from $149' },
{ value: 'correction', label: 'Full Correction - from $699' },
{ value: 'ceramic', label: 'Ceramic Coat - from $1,299' },
{ value: 'fleet', label: 'Fleet Clean (3+ vehicles) - custom quote' }];


export default function BookingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    service: '',
    date: '',
    notes: ''
  });

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Backend connection point — submit form.* to your booking API here
    setSubmitted(true);
  };

  // Get tomorrow's date as min for date picker
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <section id="book" ref={sectionRef} className="py-24 md:py-32" style={{ background: 'var(--carbon-mid)' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Info */}
          <div>
            <div className="metric-pill mb-5 reveal">Book Your Detail</div>
            <h2
              className="font-manrope font-extrabold text-white reveal reveal-delay-1"
              style={{ fontSize: 'clamp(28px, 4vw, 48px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
              
              Your detail,<br />
              <span style={{ color: 'var(--ceramic-blue)' }}>at your location.</span>
            </h2>
            <p className="mt-5 reveal reveal-delay-2 leading-relaxed" style={{ color: 'var(--aluminum-dim)', fontSize: '16px' }}>
              We confirm within 2 hours and arrive with everything needed: water, power, equipment. You don't lift a finger.
            </p>

            {/* Trust badges */}
            <div className="mt-10 space-y-4 reveal reveal-delay-3">
              {[
              { icon: '🛡️', title: 'Fully insured', sub: '$2M liability coverage on every job' },
              { icon: '📍', title: 'We come to you', sub: 'Office, garage, apartment complex' },
              { icon: '⏱️', title: '2-hour confirmation', sub: 'Booking confirmed same business day' },
              { icon: '🏢', title: 'Fleet invoicing', sub: 'Net-30 terms for corporate accounts' }].
              map((item) =>
              <div
                key={item.title}
                className="flex items-center gap-4 rounded-xl px-5 py-4"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(63,63,70,0.5)' }}>
                
                  <span style={{ fontSize: '22px' }}>{item.icon}</span>
                  <div>
                    <div className="font-manrope font-semibold text-white" style={{ fontSize: '14px' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--aluminum-dim)' }}>{item.sub}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Photo */}
            <div className="mt-10 reveal reveal-delay-4 rounded-2xl overflow-hidden" style={{ height: '220px' }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1305eda64-1766502127700.png"
                alt="Professional detailer applying ceramic coating to black luxury sedan hood under warm workshop lighting"
                fill
                className="object-cover" />
              
            </div>
          </div>

          {/* Right: Form */}
          <div className="reveal reveal-delay-2">
            {submitted ?
            <div
              className="rounded-2xl p-10 text-center flex flex-col items-center gap-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(59,130,246,0.4)' }}>
              
                <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(59,130,246,0.15)', border: '2px solid var(--ceramic-blue)' }}>
                
                  <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ color: 'var(--ceramic-blue)' }}>
                    <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="font-manrope font-extrabold text-white" style={{ fontSize: '26px' }}>
                  Booking Received
                </h3>
                <p style={{ color: 'var(--aluminum-dim)', fontSize: '15px', lineHeight: 1.6, maxWidth: '320px' }}>
                  We'll confirm your appointment within 2 hours via email. Check your inbox for next steps.
                </p>
                <div className="glow-line w-full mt-4" />
                <p style={{ fontSize: '13px', color: 'var(--aluminum-dim)' }}>
                  Questions? Text or call <span style={{ color: 'var(--white)' }}>(415) 800-DETAIL</span>
                </p>
              </div> :

            <form
              onSubmit={handleSubmit}
              className="rounded-2xl p-8 md:p-10 space-y-6"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(63,63,70,0.7)' }}>
              
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="form-label" htmlFor="name">Full Name</label>
                    <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Jordan Chen"
                    className="form-input"
                    value={form.name}
                    onChange={handleChange} />
                  
                  </div>
                  <div>
                    <label className="form-label" htmlFor="email">Email</label>
                    <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="jordan@company.com"
                    className="form-input"
                    value={form.email}
                    onChange={handleChange} />
                  
                  </div>
                </div>

                <div>
                  <label className="form-label" htmlFor="phone">Phone (for confirmation)</label>
                  <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(415) 555-0182"
                  className="form-input"
                  value={form.phone}
                  onChange={handleChange} />
                
                </div>

                <div>
                  <label className="form-label" htmlFor="vehicle">Vehicle Type</label>
                  <select
                  id="vehicle"
                  name="vehicle"
                  required
                  className="form-input"
                  value={form.vehicle}
                  onChange={handleChange}>
                  
                    <option value="" disabled>Select your vehicle</option>
                    {vehicleTypes.map((v) =>
                  <option key={v} value={v}>{v}</option>
                  )}
                  </select>
                </div>

                <div>
                  <label className="form-label" htmlFor="service">Service Tier</label>
                  <select
                  id="service"
                  name="service"
                  required
                  className="form-input"
                  value={form.service}
                  onChange={handleChange}>
                  
                    <option value="" disabled>Choose a service</option>
                    {serviceTiers.map((s) =>
                  <option key={s.value} value={s.value}>{s.label}</option>
                  )}
                  </select>
                </div>

                <div>
                  <label className="form-label" htmlFor="date">Preferred Date</label>
                  <input
                  id="date"
                  name="date"
                  type="date"
                  required
                  min={minDate}
                  className="form-input"
                  value={form.date}
                  onChange={handleChange} />
                
                </div>

                <div>
                  <label className="form-label" htmlFor="notes">Notes (optional)</label>
                  <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  placeholder="Location, access notes, specific concerns..."
                  className="form-input resize-none"
                  value={form.notes}
                  onChange={handleChange} />
                
                </div>

                <button type="submit" className="btn-primary w-full justify-center" style={{ fontSize: '16px', padding: '16px 32px' }}>
                  Confirm Booking
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <p className="text-center" style={{ fontSize: '12px', color: 'var(--aluminum-dim)' }}>
                  No payment required now. We confirm first, invoice after.
                </p>
              </form>
            }
          </div>
        </div>
      </div>
    </section>);

}